import { DownloadOutlined } from '@ant-design/icons';
import { selectContactsSourcesAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  ChannelIcon,
  ChannelType,
} from '../../../../liveChat/components/ChannelIcon';
import { ReactComponent as XrayIcon } from './icons/xray-black-white.svg';
import styles from './ContactsByChannelsStatistic.module.css';
import classNames from 'classnames';
import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { selectAudiencesPagination } from '@mm/state/selectors/messengerAdSelectors';
import { ReactComponent as AudiencesIcon } from 'assets/audiences-icon.svg';
import { Spin } from 'antd';

type Props = {
  css?: {
    container?: string;
    row?: string;
    data?: string;
    count?: string;
  };
  setIsFetching?: (value: boolean) => void;
  withAudiences?: boolean;
  busy?: boolean;
};

export const ContactsByChannelsStatistic = ({
  css,
  busy,
  withAudiences,
  setIsFetching,
}: Props) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const audiencesPagination = useSelector(selectAudiencesPagination);
  const audiencesCount = audiencesPagination.total;
  const contactsAnalytics = useSelector(selectContactsSourcesAnalytics);
  const analyticsArr = Object.entries(contactsAnalytics || {});

  const friendlyNames = (channel: string) => {
    switch (channel) {
      case 'csv':
        return 'Imported';
      case 'xray':
        return 'X-Ray';
      case 'leadsDb':
        return 'Consumer Directory';
      case 'sms':
        return 'SMS';
      default:
        return channel;
    }
  };

  const csvIcon = () => <DownloadOutlined style={{ fontSize: 20 }} />;

  const history = useHistory();

  const redirectToAudiences = () => {
    history.push(BotBuilderRoutes.audiences.getLink({ chatbotId }));
  };

  const { retrieveContactsSourcesAnalytics } = useBotAnalyticsCRUDActions();

  const fetchAnalytic = async () => {
    setIsFetching?.(true);
    await retrieveContactsSourcesAnalytics(chatbotId);
    setIsFetching?.(false);
  };

  useEffect(() => {
    if (!analyticsArr.length) {
      fetchAnalytic();
    }
  }, []);

  return (
    <div className={classNames(styles.channels, css?.container)}>
      {busy ? (
        <Spin />
      ) : (
        analyticsArr
          .filter((item: [string, number]) => item[1] > 0)
          .map((item: [string, number]) => {
            const channel = item[0] === 'mercury' ? 'leadsDb' : item[0];
            return (
              <div
                className={classNames(styles.channelRow, css?.row)}
                key={item[0]}
              >
                <span className={classNames(styles.dataRow, css?.data)}>
                  <div className={classNames(styles.count, css?.count)}>
                    <span>{friendlyNames(channel)}</span>
                    <span> {item[1].toLocaleString()}</span>
                  </div>
                  <ChannelIcon
                    channel={channel as ChannelType}
                    customMap={{
                      csv: csvIcon,
                      xray: XrayIcon,
                    }}
                    iconSize={20}
                  />
                </span>
              </div>
            );
          })
      )}

      {withAudiences && (
        <div className={classNames(styles.channelRow, css?.row)}>
          <div className={classNames(styles.dataColumn, css?.count)}>
            <span className={styles.link} onClick={redirectToAudiences}>
              Audiences{' '}
            </span>
            <span className={classNames(styles.count)}>{audiencesCount}</span>
          </div>

          <AudiencesIcon />
        </div>
      )}
    </div>
  );
};
